import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { invoiceService, customerService, branchService, planService } from '../services/api';
import Card from '../components/Card';
import {
  FiDollarSign, FiShoppingCart, FiPackage, FiUsers,
  FiMapPin, FiCheckCircle, FiTrendingUp, FiActivity,
  FiArrowRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Area, AreaChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

/* ── mock weekly data ── */
const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const mockWeeklySales = weekDays.map((d) => ({
  day: d,
  sales: Math.floor(Math.random() * 800 + 200),
  transactions: Math.floor(Math.random() * 40 + 10),
}));

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="text-xs">
            {p.name}: <span className="font-semibold">{p.name === 'sales' ? `$${p.value}` : p.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const QuickAction = ({ label, description, path, navigate }) => (
  <button
    onClick={() => navigate(path)}
    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-purple-50 border border-gray-100 hover:border-purple-200 rounded-xl transition-all group"
  >
    <div className="text-left">
      <p className="text-sm font-semibold text-gray-800 group-hover:text-purple-700">{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{description}</p>
    </div>
    <FiArrowRight size={16} className="text-gray-300 group-hover:text-purple-500 transition-colors" />
  </button>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [resellerStats, setResellerStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (['branch_manager', 'cashier', 'staff'].includes(user?.role)) {
      fetchStats();
    } else if (user?.role === 'reseller') {
      fetchResellerStats();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await invoiceService.getDashboardStats();
      setStats(res.data.data);
    } catch {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchResellerStats = async () => {
    try {
      const [mRes, bRes, pRes] = await Promise.all([
        customerService.getAll(),
        branchService.getAll(),
        planService.getAll(),
      ]);
      const merchants = mRes.data.data || [];
      const branches = bRes.data.data || [];
      const plans = pRes.data.data || [];
      setResellerStats({
        totalMerchants: merchants.length,
        activeMerchants: merchants.filter((m) => m.isActive && !m.isSuspended).length,
        totalBranches: branches.length,
        totalPlans: plans.length,
      });
    } catch {
      toast.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {greeting()}, {user?.fullName?.split(' ')[0]}!
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Here's what's happening today.</p>
      </div>

      {/* ── Super Admin ── */}
      {user?.role === 'superadmin' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <FiActivity size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">System Overview</h2>
                <p className="text-xs text-gray-400">Super Admin Control Panel</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              You have full control over the platform. Manage resellers, monitor system health,
              and configure global settings from one place.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <QuickAction label="Manage Resellers" description="Add or edit reseller accounts" path="/resellers" navigate={navigate} />
              <QuickAction label="System Settings" description="Configure global preferences" path="/settings" navigate={navigate} />
            </div>
          </div>
          <div className="rounded-xl p-6 text-white flex flex-col justify-between" style={{ background: 'linear-gradient(to bottom right, rgb(130, 104, 178), #6a5294)' }}>
            <div>
              <p className="text-xs font-semibold text-purple-200 uppercase tracking-wide mb-1">Platform Status</p>
              <p className="text-2xl font-bold">Active</p>
            </div>
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-purple-100 text-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                All services operational
              </div>
              <div className="flex items-center gap-2 text-purple-100 text-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                API endpoints healthy
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reseller ── */}
      {user?.role === 'reseller' && resellerStats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card title="Total Merchants" value={resellerStats.totalMerchants} icon={FiUsers} color="blue" />
            <Card title="Active Merchants" value={resellerStats.activeMerchants} icon={FiCheckCircle} color="green" />
            <Card title="Total Branches" value={resellerStats.totalBranches} icon={FiMapPin} color="yellow" />
            <Card title="Active Plans" value={resellerStats.totalPlans} icon={FiPackage} color="purple" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Bar chart */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Weekly Overview</h3>
                  <p className="text-xs text-gray-400">Merchant activity this week</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500 inline-block" /> Merchants</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekDays.map((d, i) => ({ day: d, value: Math.floor(Math.random() * resellerStats.totalMerchants + 1) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#8268b2" radius={[6, 6, 0, 0]} maxBarSize={36} name="merchants" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Quick actions */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-card p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <QuickAction label="Add Merchant" description="Onboard a new merchant" path="/customers" navigate={navigate} />
                <QuickAction label="Create Plan" description="Set up a subscription plan" path="/plans" navigate={navigate} />
                <QuickAction label="View Branches" description="See all merchant branches" path="/customers" navigate={navigate} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Customer / Merchant ── */}
      {user?.role === 'customer' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                <FiMapPin size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Merchant Dashboard</h2>
                <p className="text-xs text-gray-400">Manage your business locations</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Configure your branches, manage staff accounts, set up products and track sales
              across all your business locations.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <QuickAction label="Manage Branches" description="Create or configure branches" path="/branches" navigate={navigate} />
              <QuickAction label="Settings" description="Account & business settings" path="/settings" navigate={navigate} />
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white flex flex-col justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-100 uppercase tracking-wide mb-1">Account Status</p>
              <p className="text-2xl font-bold">Active</p>
            </div>
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-emerald-100 text-sm">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                Subscription active
              </div>
              <div className="flex items-center gap-2 text-emerald-100 text-sm">
                <div className="w-2 h-2 bg-white rounded-full" />
                POS system ready
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Branch Manager / Cashier / Staff ── */}
      {['branch_manager', 'cashier', 'staff'].includes(user?.role) && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              title="Today's Sales"
              value={`$${stats.todaySales?.toFixed(2) || '0.00'}`}
              icon={FiDollarSign}
              color="green"
              subtitle="Revenue collected today"
            />
            <Card
              title="Transactions"
              value={stats.todayTransactions || 0}
              icon={FiShoppingCart}
              color="blue"
              subtitle="Completed today"
            />
            <Card
              title="Total Products"
              value={stats.totalProducts || 0}
              icon={FiPackage}
              color="purple"
              subtitle="In inventory"
            />
          </div>

          {/* Sales chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Weekly Sales Trend</h3>
                <p className="text-xs text-gray-400">Sales performance over the last 7 days</p>
              </div>
              <div className="text-xs bg-purple-50 text-purple-600 font-medium px-2.5 py-1 rounded-full">
                This Week
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={mockWeeklySales}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8268b2" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#8268b2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="sales" stroke="#8268b2" strokeWidth={2} fill="url(#salesGrad)" name="sales" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <QuickAction label="Go to POS" description="Start selling now" path="/pos" navigate={navigate} />
            <QuickAction label="Manage Products" description="View inventory" path="/products" navigate={navigate} />
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
